import * as React from "react";

import { Tag } from "@replo/design-system/components/tag/Tag";
import { ExperimentActionsBar } from "@v1/features/experiments/components/ExperimentActionsBar";
import { experimentStatusMapping } from "@v1/features/experiments/experimentStatus";
import { ExperimentsLayout } from "@v1/features/experiments/Layout";
import { ExperimentsEditTabV2 } from "@v1/features/experiments/tabs/ExperimentsEditTabV2";
import { ExperimentTabWrapper } from "@v1/features/experiments/tabs/ExperimentTabWrapper";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { experimentRouter } from "@v2/trpc/routes/experiment";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { getExperimentStatus } from "schemas/experiment";

import { ExperimentEditableHeader } from "../ExperimentEditableHeader";
import { useExperimentEdit } from "./hooks/useExperimentEdit";

const ExperimentDetailsTab: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const projectId = useCurrentProjectId();
  const { experimentId } = useParams();
  const { dispatchExperimentEdit, handleReactHookFormSubmit } =
    useExperimentEdit();
  const navigate = useNavigate();

  const {
    data: { experiment } = {
      experiment: undefined,
    },
    isFetching: isFetchingExperiments,
  } = experimentRouter.findDetail.useQuery(
    {
      experimentId: experimentId ?? "",
      workspaceId: workspaceId ?? undefined,
      projectId,
    },
    {
      enabled:
        Boolean(experimentId) && (Boolean(workspaceId) || Boolean(projectId)),
    },
  );

  if (!experiment || (!workspaceId && !projectId) || isFetchingExperiments) {
    return null;
  }

  const experimentsListPath = Boolean(projectId)
    ? generatePath(routes.shops.experiments.list, {
        projectId,
      })
    : generatePath(routes.workspace.experiments.list, {
        workspaceId,
      });

  return (
    <ExperimentsLayout
      headerTitle={
        <div className="flex flex-row items-center gap-1">
          <ExperimentEditableHeader
            title={experiment.name}
            experimentId={experiment.id}
            dispatchExperimentEdit={dispatchExperimentEdit}
            handleReactHookFormSubmit={handleReactHookFormSubmit}
            isLoadingRequiredData={!workspaceId}
          />
          {experiment && (
            <Tag
              variant={
                experimentStatusMapping[getExperimentStatus(experiment)].variant
              }
              size="base"
            >
              {experimentStatusMapping[getExperimentStatus(experiment)].label}
            </Tag>
          )}
        </div>
      }
      headerActions={<ExperimentActionsBar experiment={experiment} />}
      onBackClick={() => {
        navigate(experimentsListPath);
      }}
    >
      <ExperimentTabWrapper className="max-w-[800px]">
        <ExperimentsEditTabV2 experiment={experiment} />
      </ExperimentTabWrapper>
    </ExperimentsLayout>
  );
};

export default ExperimentDetailsTab;
